import {toInt} from '@/library/math';
import {mailSendTypes} from '@/library/modules/enrolment/mail';
import {ROUTE_REF_PARAM_DEFINITION} from '@/utils/portuna';
import {type RouteLocationStringParam, type RouteRecordRaw} from 'vue-router';

const EmployeeAssistant      = () => import('../views/employee/components/assistant/EmployeeAssistant.vue');
const EmployeeAssistantIntro = () => import('../views/employee/components/assistant/EmployeeAssistantIntro.vue');
const EmployeeAssistantOutro = () => import('../views/employee/components/assistant/EmployeeAssistantOutro.vue');

const EnrolmentAssistant      = () => import('../views/enrolment/components/assistant/EnrolmentAssistant.vue');
const EnrolmentAssistantIntro = () => import('../views/enrolment/components/assistant/EnrolmentAssistantIntro.vue');
const EnrolmentAssistantOutro = () => import('../views/enrolment/components/assistant/EnrolmentAssistantOutro.vue');

const EnrolmentIndex                   = () => import('../views/enrolment/index/EnrolmentIndex.vue');
const EnrolmentDashboard               = () => import('../views/enrolment/index/EnrolmentDashboard.vue');
const EnrolmentPipelinePhase           = () => import('../views/enrolment/index/EnrolmentPipelinePhase.vue');
const EnrolmentPipelineApplication     = () => import('../views/enrolment/index/EnrolmentPipelineApplication.vue');
const EnrolmentPipelineApplicationEdit = () => import('../views/enrolment/index/EnrolmentPipelineApplicationEdit.vue');

const EnrolmentList = () => import('../views/enrolment/enrolment/EnrolmentList.vue');
const EnrolmentView = () => import('../views/enrolment/enrolment/EnrolmentView.vue');
const EnrolmentEdit = () => import('../views/enrolment/enrolment/EnrolmentEdit.vue');

const ApplicationList        = () => import('../views/enrolment/application/ApplicationList.vue');
const ApplicationChangeState = () => import('../views/enrolment/application/ApplicationChangeState.vue');
const ApplicationChangeLog   = () => import('../views/enrolment/application/ApplicationChangeLog.vue');
const ApplicationAdd         = () => import('../views/enrolment/application/ApplicationAdd.vue');
const ApplicationView        = () => import('../views/enrolment/application/ApplicationView.vue');
const ApplicationViewEdit    = () => import('../views/enrolment/application/ApplicationViewEdit.vue');

const MailIndex = () => import('../views/enrolment/mail/MailIndex.vue');
const MailList  = () => import('../views/enrolment/mail/MailList.vue');
const MailView  = () => import('../views/enrolment/mail/MailView.vue');
const MailEdit  = () => import('../views/enrolment/mail/MailEdit.vue');

const MailboxEdit             = () => import('../views/enrolment/settings/mailbox/MailboxEdit.vue');
const MailTemplateEdit        = () => import('../views/enrolment/settings/mailTemplate/MailTemplateEdit.vue');
const SignatureEdit           = () => import('../views/enrolment/settings/signature/SignatureEdit.vue');
const FormTemplateEdit        = () => import('../views/enrolment/settings/webExportTemplate/FormTemplateEdit.vue');

const AppointmentList = () => import('../views/enrolment/appointment/AppointmentList.vue');
const AppointmentView = () => import('../views/enrolment/appointment/AppointmentView.vue');

const WebExportEnrolmentOverviewList = () => import('../views/enrolment/enrolment/exportOverview/WebExportEnrolmentOverviewList.vue');
const WebExportEnrolmentOverviewView = () => import('../views/enrolment/enrolment/exportOverview/WebExportEnrolmentOverviewView.vue');

const WebExportEnrolmentList   = () => import('../views/enrolment/enrolment/export/WebExportEnrolmentList.vue');
const WebExportEnrolmentView   = () => import('../views/enrolment/enrolment/export/WebExportEnrolmentView.vue');
const WebExportApplicationEdit = () => import('../views/enrolment/enrolment/export/WebExportApplicationEdit.vue');

let meta: MetaData = {
    module: 'employee',
    assistant: {
        key: 'enrolment',
        steps: [
            {
                component: EmployeeAssistantIntro,
            },
            {
                title: 'Personal',
                titleIcon: 'support',
                component: EmployeeAssistant,
            },
            {
                component: EmployeeAssistantOutro,
            },
            {
                component: EnrolmentAssistantIntro,
            },
            {
                title: 'Bewerbungen',
                titleIcon: 'support',
                component: EnrolmentAssistant,
            },
            {
                component: EnrolmentAssistantOutro,
            },
        ],
    },
};

let enrolment: RouteRecordRaw[] = [
    // <editor-fold desc="Enrolment">
    {
        name: 'enrolment',
        path: '/employee/enrolment/index',
        redirect: {name: 'enrolment-dashboard'},
        meta,
    },
    {
        name: 'enrolment-index',
        path: '/employee/enrolment',
        component: EnrolmentIndex,
        redirect: {name: 'enrolment-dashboard'},
        meta,
        children: [
            {
                name: 'enrolment-dashboard',
                path: 'dashboard',
                component: EnrolmentDashboard,
                meta,
            },
            {
                name: 'enrolment-pipeline-phase',
                path: 'pipeline/phase/:phaseId/:phaseChildId', // @todo rathmann: optimize child-routing regarding optional phaseChildId param
                component: EnrolmentPipelinePhase,
                meta,
                children: [
                    {
                        name: 'enrolment-pipeline-phase-application',
                        path: 'application/:applicationId/:tab?',
                        component: EnrolmentPipelineApplication,
                        meta,
                        props(route: RouteLocationStringParam)
                        {
                            return {
                                applicationId: parseInt(route.params.applicationId) || 0,
                            };
                        },
                        children: [
                            {
                                name: 'enrolment-pipeline-phase-application-edit',
                                path: 'edit',
                                component: EnrolmentPipelineApplicationEdit,
                                meta,
                                props(route: RouteLocationStringParam)
                                {
                                    return {
                                        applicationId: parseInt(route.params.applicationId) || 0,
                                    };
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        name: 'enrolment-list',
        path: '/employee/enrolment/enrolment/list',
        component: EnrolmentList,
        meta,
    },
    {
        name: 'enrolment-view',
        path: '/employee/enrolment/enrolment/view/id/:id',
        component: EnrolmentView,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id) || 0,
            };
        },
    },
    {
        name: 'enrolment-edit',
        path: '/employee/enrolment/enrolment/edit/id/:id/:tab?',
        component: EnrolmentEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id) || 0,
                mode: 'edit',
            };
        },
    },
    {
        name: 'enrolment-copy',
        path: '/employee/enrolment/enrolment/copy/id/:id/:tab?',
        component: EnrolmentEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id) || 0,
                mode: 'copy',
            };
        },
    },
    {
        name: 'enrolment-add',
        path: '/employee/enrolment/enrolment/add/:tab?',
        component: EnrolmentEdit,
        meta,
        props: {
            mode: 'add',
        },
    },
    //</editor-fold>

    //<editor-fold desc="Application">
    {
        name: 'enrolment-application-list',
        path: '/employee/enrolment/application/list/:role?/:phase?/:employee?/:enrolmentId?',
        component: ApplicationList,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                role: route.params.role || 'access',
                phase: route.params.phase || '',
                employee: parseInt(route.params.employee) || 0,
                enrolmentId: parseInt(route.params.enrolmentId) || 0,
            };
        },
    },
    //<editor-fold desc="New Application">
    {
        name: 'enrolment-application-add',
        path: '/employee/enrolment/application/add/:role?',
        component: ApplicationAdd,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                role: route.params.role || 'access',
            };
        },
    },
    //</editor-fold>
    //<editor-fold desc="New Application From E-Mail">
    {
        name: 'enrolment-application-add-from-mail',
        path: '/employee/enrolment/application/add/mailId/:mailId',
        component: ApplicationAdd,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                mailId: parseInt(route.params.mailId) || 0,
            };
        },
    },
    //</editor-fold>
    //<editor-fold desc="New Application From Enrolment">
    {
        name: 'enrolment-application-add-from-enrolment',
        path: '/employee/enrolment/application/add/enrolmentId/:enrolmentId',
        meta,
        component: ApplicationAdd,
        props(route: RouteLocationStringParam)
        {
            return {
                enrolmentId: parseInt(route.params.enrolmentId) || 0,
            };
        },
    },
    //</editor-fold>
    {
        name: 'enrolment-application-view',
        path: '/employee/enrolment/application/view/id/:applicationId/:role?',
        component: ApplicationView,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                applicationId: parseInt(route.params.applicationId) || 0,
                role: route.params.role || 'access',
            };
        },
        children: [
            {
                name: 'enrolment-application-view-edit',
                path: 'edit',
                component: ApplicationViewEdit,
                meta,
                props(route: RouteLocationStringParam)
                {
                    return {
                        applicationId: parseInt(route.params.applicationId) || 0,
                        role: route.params.role || 'access',
                    };
                },
            },
        ],
    },
    {
        name: 'enrolment-application-change-state',
        path: '/employee/enrolment/application/change-state/id/:applicationId/:role?',
        component: ApplicationChangeState,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                applicationId: parseInt(route.params.applicationId) || 0,
                role: route.params.role || 'access',
            };
        },
    },
    {
        name: 'enrolment-application-change-log',
        path: '/employee/enrolment/application/change-log/id/:applicationId/:role?',
        component: ApplicationChangeLog,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                applicationId: parseInt(route.params.applicationId) || 0,
                role: route.params.role || 'access',
            };
        },
    },
    //</editor-fold>

    //<editor-fold desc="E-Mail">
    {
        name: 'enrolment-mail-list',
        path: '/employee/enrolment/mail/list',
        redirect: {
            name: 'enrolment-mail-folder',
            params: {folder: '0'},
        },
        component: MailIndex,
        meta,
        children: [
            {
                name: 'enrolment-mail-folder',
                path: ':folder',
                component: MailList,
                meta,
                props(route: RouteLocationStringParam)
                {
                    return {
                        folder: parseInt(route.params.folder),
                    };
                },
            },
        ],
    },
    {
        name: 'enrolment-mail-view',
        path: '/employee/enrolment/mail/view/id/:id',
        component: MailView,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
            };
        },
    },
    {
        name: 'enrolment-mail-new',
        path: '/employee/enrolment/mail/create/:applicationId(\\d+)?/' + ROUTE_REF_PARAM_DEFINITION,
        component: MailEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                sendType: mailSendTypes.new,
                applicationId: parseInt(route.params.applicationId) || 0,
            };
        },
    },
    {
        name: 'enrolment-mail-reply',
        path: '/employee/enrolment/mail/reply/:mailId/:applicationId(\\d+)?/' + ROUTE_REF_PARAM_DEFINITION,
        component: MailEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                sendType: mailSendTypes.reply,
                mailId: parseInt(route.params.mailId),
                applicationId: parseInt(route.params.applicationId) || 0,
            };
        },
    },
    {
        name: 'enrolment-mail-reply-all',
        path: '/employee/enrolment/mail/reply-all/:mailId/:applicationId(\\d+)?/' + ROUTE_REF_PARAM_DEFINITION,
        component: MailEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                sendType: mailSendTypes.replyAll,
                mailId: parseInt(route.params.mailId),
                applicationId: parseInt(route.params.applicationId) || 0,
            };
        },
    },
    {
        name: 'enrolment-mail-forward',
        path: '/employee/enrolment/mail/forward/:mailId/:applicationId(\\d+)?/' + ROUTE_REF_PARAM_DEFINITION,
        component: MailEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                sendType: mailSendTypes.forward,
                mailId: parseInt(route.params.mailId),
                applicationId: parseInt(route.params.applicationId) || 0,
            };
        },
    },
    {
        name: 'enrolment-mail-edit-draft',
        path: '/employee/enrolment/mail/edit-draft/:id/:applicationId(\\d+)?/' + ROUTE_REF_PARAM_DEFINITION,
        component: MailEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
                applicationId: parseInt(route.params.applicationId) || 0,
            };
        },
    },
    //</editor-fold>

    //<editor-fold desc="Old Settings">
    {
        name: 'enrolment-mailbox-add',
        path: '/employee/enrolment/mailbox/add',
        component: MailboxEdit,
        meta,
    },
    {
        name: 'enrolment-mailbox-edit',
        path: '/employee/enrolment/mailbox/edit/:id',
        component: MailboxEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
            };
        },
    },
    {
        name: 'enrolment-mailbox-copy',
        path: '/employee/enrolment/mailbox/copy/:copyId',
        component: MailboxEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                copyId: parseInt(route.params.copyId),
            };
        },
    },
    {
        name: 'enrolment-mail-template-add',
        path: '/employee/enrolment/mail-template/add',
        component: MailTemplateEdit,
        meta,
    },
    {
        name: 'enrolment-mail-template-edit',
        path: '/employee/enrolment/mail-template/edit/:id',
        component: MailTemplateEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
            };
        },
    },
    {
        name: 'enrolment-mail-template-copy',
        path: '/employee/enrolment/mail-template/copy/:copyId',
        component: MailTemplateEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                copyId: parseInt(route.params.copyId),
            };
        },
    },
    {
        name: 'enrolment-signature-add',
        path: '/employee/enrolment/signature/add',
        component: SignatureEdit,
        meta,
    },
    {
        name: 'enrolment-signature-edit',
        path: '/employee/enrolment/signature/edit/:id',
        component: SignatureEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
            };
        },
    },
    {
        name: 'enrolment-form-template-add',
        path: '/employee/enrolment/form-template/add',
        component: FormTemplateEdit,
        meta,
    },
    {
        name: 'enrolment-form-template-edit',
        path: '/employee/enrolment/form-template/edit/:id',
        component: FormTemplateEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
            };
        },
    },
    {
        name: 'enrolment-form-template-copy',
        path: '/employee/enrolment/form-template/copy/:copyId',
        component: FormTemplateEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                copyId: parseInt(route.params.copyId),
            };
        },
    },
    //</editor-fold>

    {
        name: 'enrolment-appointment-list',
        path: '/employee/enrolment/appointment/list',
        component: AppointmentList,
        meta,
    },
    {
        name: 'enrolment-appointment-view',
        path: '/employee/enrolment/appointment/view/id/:id',
        component: AppointmentView,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
            };
        },
    },
    {
        name: 'enrolment-web-export-overview-list',
        path: '/employee/enrolment/web-export-overview/list',
        component: WebExportEnrolmentOverviewList,
        meta,
    },
    {
        name: 'enrolment-web-export-overview-view',
        path: '/employee/enrolment/web-export-overview/view/id/:id',
        component: WebExportEnrolmentOverviewView,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
            };
        },
    },
    {
        name: 'enrolment-web-export-list-view',
        path: '/employee/enrolment/web-export/list/id/:id?',
        alias: '/employee/enrolment/web-export/list',
        component: WebExportEnrolmentList,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: 'undefined' !== typeof route.params.id ? toInt(route.params.id) : 0,
            };
        },
    },
    {
        name: 'enrolment-web-export-view',
        path: '/employee/enrolment/web-export/view/id/:id',
        component: WebExportEnrolmentView,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
            };
        },
    },
    {
        name: 'enrolment-web-export-form',
        path: '/employee/enrolment/web-export/edit/id/:id',
        component: WebExportApplicationEdit,
        meta,
        props(route: RouteLocationStringParam)
        {
            return {
                id: parseInt(route.params.id),
            };
        },
    },
];

export default enrolment;